// mds-dojo — dojo/store/Memory. In-memory object store keyed by idProperty.
// Implements the slice of the store API the oracle uses: get/put/add/remove/query.

function queryEngine(query: any) {
  // A function query is a per-ITEM predicate (dojo SimpleQueryEngine: array.filter(query)),
  // NOT a function of the whole array.
  if (typeof query === 'function') { return (data: any[]) => data.filter(query as any); }
  return function (data: any[]) {
    if (!query || Object.keys(query).length === 0) { return data.slice(); }
    return data.filter((obj) => {
      for (const k in query) {
        const required = query[k];
        // dojo SimpleQueryEngine: a RegExp value matches via .test() (this is what
        // ComboBox/FilteringSelect/_AutoCompleterMixin rely on for live filtering);
        // a function value is a per-key predicate; otherwise strict equality.
        if (required != null && typeof required.test === 'function' && !(required instanceof Date)) {
          if (!required.test(obj[k], obj)) { return false; }
        } else if (typeof required === 'function') {
          if (!required(obj[k], obj)) { return false; }
        } else if (required !== obj[k]) {
          return false;
        }
      }
      return true;
    });
  };
}

export default class Memory {
  data: any[] = [];
  index: Record<string, number> = {};
  idProperty = 'id';

  constructor(options?: any) {
    if (options) { for (const k in options) { (this as any)[k] = options[k]; } }
    this.setData(this.data || []);
  }

  get(id: any): any { return this.data[this.index[id]]; }

  getIdentity(object: any): any { return object[this.idProperty]; }

  put(object: any, options?: any): any {
    const id = (options && 'id' in options) ? options.id : object[this.idProperty];
    if (id != null && id in this.index) {
      this.data[this.index[id]] = object;
    } else {
      const newId = id != null ? id : (object[this.idProperty] = this.data.length ? Math.max.apply(null, this.data.map((d) => Number(d[this.idProperty]) || 0)) + 1 : 1);
      this.index[newId] = this.data.length;
      this.data.push(object);
    }
    return object[this.idProperty];
  }

  add(object: any, options?: any): any {
    const id = (options && 'id' in options) ? options.id : object[this.idProperty];
    if (id != null && id in this.index) {
      throw new Error('Object already exists: ' + id);
    }
    return this.put(object, options);
  }

  remove(id: any): boolean {
    if (id in this.index) {
      this.data.splice(this.index[id], 1);
      this.buildIndex();
      return true;
    }
    return false;
  }

  query(query?: any, options?: any): any[] {
    let results = queryEngine(query)(this.data);
    options = options || {};
    const sort = options.sort;
    if (sort) {
      results.sort(typeof sort === 'function' ? sort : (a: any, b: any) => {
        for (const s of sort) {
          const av = a[s.attribute];
          const bv = b[s.attribute];
          if (av !== bv) { return (!!s.descending === (av > bv)) ? -1 : 1; }
        }
        return 0;
      });
    }
    const total = results.length;
    if (options.start || options.count != null) {
      const start = options.start || 0;
      results = results.slice(start, options.count == null ? undefined : start + options.count);
    }
    (results as any).total = total;
    return results;
  }

  setData(data: any[]): void {
    this.data = data;
    this.buildIndex();
  }

  private buildIndex(): void {
    this.index = {};
    for (let i = 0; i < this.data.length; i++) {
      this.index[this.data[i][this.idProperty]] = i;
    }
  }
}
